import type { CreateGhostProfileData } from '@hooks'
// Imported from the source module (not the '@utils' barrel) so this pure parser
// stays unit-testable without pulling in the toast/UI config the barrel loads.
import { Scope } from '../../../../utils/constants'

// Reads a CRM export (CSV only) into a header row + data rows, auto-detects
// which column maps to which contact field (French + English synonyms), and
// turns each row into a ready-to-create ghost-profile payload.
//
// CSV-only by design: the previous Excel path used SheetJS (`xlsx`), affected by
// CVE-2023-30533 (prototype pollution during XLSX.read) and CVE-2024-22363
// (ReDoS) with no patched build on the npm registry. Parsing untrusted uploads
// with it was an unacceptable attack surface, so the binary parser was dropped
// in favour of the hand-rolled, safe CSV reader below.

// Thrown when a non-CSV file (e.g. .xlsx/.xls) reaches the parser. The UI catches
// this sentinel and shows a translated "unsupported format" message.
export const UNSUPPORTED_FORMAT_ERROR = 'UNSUPPORTED_FORMAT'

export type CanonicalField =
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'email'
  | 'phone'
  | 'company'
  | 'jobTitle'
  | 'website'
  | 'linkedin'
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'notes'

export interface ParsedFile {
  headers: string[]
  rows: string[][]
  // headerIndex -> canonical field (or null when no confident match)
  mapping: (CanonicalField | null)[]
}

// Accent-insensitive, punctuation-insensitive normalisation for header matching.
const normalize = (s: string): string =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

// Ordered list of synonyms per field. First confident match wins.
const SYNONYMS: Record<CanonicalField, string[]> = {
  firstName: ['prenom', 'firstname', 'givenname', 'first'],
  lastName: ['nomdefamille', 'lastname', 'surname', 'familyname', 'nom'],
  fullName: ['nomcomplet', 'fullname', 'name', 'contact', 'nomprenom', 'prenomnom'],
  email: ['email', 'mail', 'courriel', 'adressemail', 'emailaddress', 'mailaddress'],
  phone: [
    'telephone', 'phone', 'mobile', 'gsm', 'tel', 'numero', 'portable',
    'phonenumber', 'numerodetelephone', 'tellphone', 'cell', 'cellphone',
  ],
  company: [
    'entreprise', 'societe', 'company', 'organization', 'organisation',
    'employer', 'employeur', 'compagnie', 'companyname', 'raisonsociale',
  ],
  jobTitle: ['poste', 'fonction', 'profession', 'metier', 'jobtitle', 'title', 'job', 'role', 'titre'],
  website: ['site', 'siteweb', 'website', 'web', 'url', 'siteinternet'],
  linkedin: ['linkedin', 'linkedinurl', 'profillinkedin'],
  instagram: ['instagram', 'insta', 'instagramhandle'],
  facebook: ['facebook', 'fb', 'facebookurl'],
  twitter: ['twitter', 'x', 'twitterhandle'],
  notes: ['notes', 'note', 'description', 'bio', 'remarque', 'remarques', 'commentaire', 'commentaires'],
}

// Field priority so that, e.g. "nom" doesn't steal the slot from "nom complet"
// when both could match: more specific fields are resolved first.
const FIELD_ORDER: CanonicalField[] = [
  'firstName', 'lastName', 'fullName', 'email', 'phone', 'company',
  'jobTitle', 'website', 'linkedin', 'instagram', 'facebook', 'twitter', 'notes',
]

export const detectMapping = (headers: string[]): (CanonicalField | null)[] => {
  const normalized = headers.map(normalize)
  const mapping: (CanonicalField | null)[] = headers.map(() => null)
  const used = new Set<number>()
  const matched = new Set<CanonicalField>()

  const assign = (field: CanonicalField, idx: number) => {
    mapping[idx] = field
    used.add(idx)
    matched.add(field)
  }

  // Pass 1 — exact header matches for every field. Done first across ALL fields
  // so a specific header like "nom complet" maps to fullName before a looser
  // "contains" rule (lastName ⊃ "nom") could steal it.
  for (const field of FIELD_ORDER) {
    const idx = normalized.findIndex((h, i) => !used.has(i) && SYNONYMS[field].includes(h))
    if (idx !== -1) assign(field, idx)
  }

  // Pass 2 — looser "contains" matches for fields still unmatched, so "emailpro"
  // or "telephone mobile" are still recognised.
  for (const field of FIELD_ORDER) {
    if (matched.has(field)) continue
    const idx = normalized.findIndex(
      (h, i) => !used.has(i) && SYNONYMS[field].some((syn) => h.includes(syn)),
    )
    if (idx !== -1) assign(field, idx)
  }

  return mapping
}

const splitCsvLine = (line: string, delimiter: string): string[] => {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else inQuotes = false
      } else cur += c
    } else if (c === '"') inQuotes = true
    else if (c === delimiter) { out.push(cur); cur = '' }
    else cur += c
  }
  out.push(cur)
  return out.map((v) => v.trim())
}

const parseCsv = (text: string): { headers: string[]; rows: string[][] } => {
  // Strip a leading UTF-8 BOM that Excel often adds.
  const clean = text.replace(/^﻿/, '')
  const lines = clean.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return { headers: [], rows: [] }
  // Guess the delimiter from the header line (Excel-FR exports use ";").
  const semis = (lines[0].match(/;/g) || []).length
  const commas = (lines[0].match(/,/g) || []).length
  const delimiter = semis > commas ? ';' : ','
  const headers = splitCsvLine(lines[0], delimiter)
  const rows = lines.slice(1).map((l) => splitCsvLine(l, delimiter))
  return { headers, rows }
}

export const parseContactsFile = async (file: File): Promise<ParsedFile> => {
  const name = (file.name || '').toLowerCase()
  const isCsv = name.endsWith('.csv') || file.type === 'text/csv'
  // CSV is the only supported (and safe) format — reject Excel/binary uploads.
  if (!isCsv) throw new Error(UNSUPPORTED_FORMAT_ERROR)
  const { headers, rows } = parseCsv(await file.text())
  // Drop fully-empty rows.
  const nonEmpty = rows.filter((r) => r.some((c) => c && c.trim().length > 0))
  return { headers, rows: nonEmpty, mapping: detectMapping(headers) }
}

const cell = (
  row: string[],
  mapping: (CanonicalField | null)[],
  field: CanonicalField,
): string => {
  const idx = mapping.indexOf(field)
  return idx === -1 ? '' : (row[idx] || '').trim()
}

// Turns one parsed row into a ghost-profile payload. Returns null when the row
// has no usable identity (no name, email or phone) so it can be skipped.
export const rowToGhostProfile = (
  row: string[],
  mapping: (CanonicalField | null)[],
): CreateGhostProfileData | null => {
  let firstName = cell(row, mapping, 'firstName')
  let lastName = cell(row, mapping, 'lastName')
  const full = cell(row, mapping, 'fullName')
  if (!firstName && !lastName && full) {
    const parts = full.split(/\s+/)
    firstName = parts.shift() || ''
    lastName = parts.join(' ')
  }

  const email = cell(row, mapping, 'email')
  const phone = cell(row, mapping, 'phone')
  const company = cell(row, mapping, 'company')
  const jobTitle = cell(row, mapping, 'jobTitle')
  const website = cell(row, mapping, 'website')
  const linkedin = cell(row, mapping, 'linkedin')
  const instagram = cell(row, mapping, 'instagram')
  const facebook = cell(row, mapping, 'facebook')
  const twitter = cell(row, mapping, 'twitter')
  const notes = cell(row, mapping, 'notes')

  if (!firstName && !lastName && !email && !phone) return null

  // The ghost profile only stores professions by id, so a free-text job title
  // is preserved in the bio alongside any notes column.
  const bioParts = [jobTitle && `Poste : ${jobTitle}`, notes].filter(Boolean)

  const contacts = { email, phone, website, linkedin, instagram, facebook, twitter }
  const hasContact = Object.values(contacts).some((v) => v)

  return {
    scope: Scope.PRIVATE,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    company: company || undefined,
    bio: bioParts.length ? bioParts.join('\n') : undefined,
    contacts: hasContact
      ? {
          email: email || null,
          phone: phone || null,
          website: website || null,
          linkedin: linkedin || null,
          instagram: instagram || null,
          facebook: facebook || null,
          twitter: twitter || null,
        }
      : undefined,
  }
}
